import { ref } from 'vue'
import { initializeChatPush, updateChatBadge, clearChatPushBinding } from './chatPush'
import { ElNotification } from 'element-plus'
import 'element-plus/es/components/notification/style/css'
import { getAuthToken, getAuthDeviceId, isAuthValid, getAuthUser, saveAuth, getAuthExpiresAt } from '../auth'

export const chatUnread = ref(0)
export const chatEnabled = ref(true)
export const chatConnection = ref('disconnected')
let socket = null
let retryTimer = null
let heartbeat = null
let refreshTimer = null
let fallbackTimer = null
let generation = 0
let attempts = 0
let running = false
let baseline = false
let lastAttention = 0
let lastFrame = 0

export const chatApi = async (path, options = {}) => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), options.body instanceof Blob ? 45000 : 15000)
  try {
    const response = await fetch(`/api/chat${path}`, {
      ...options, signal: controller.signal,
      headers: { ...(options.body instanceof Blob ? {} : { 'Content-Type': 'application/json' }), ...options.headers },
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      const error = new Error(typeof data.detail === 'string' ? data.detail : '请求失败，请稍后重试')
      error.status = response.status
      if (data.code === 'communication_disabled') applyChatEnabled(false)
      throw error
    }
    return data
  } catch (error) {
    if (error.name === 'AbortError') throw new Error('请求超时，请重试')
    throw error
  } finally { clearTimeout(timer) }
}

const applyChatEnabled = enabled => {
  const changed = chatEnabled.value !== enabled
  chatEnabled.value = enabled
  if (!enabled) {
    clearTimeout(retryTimer)
    clearTimeout(refreshTimer)
    clearInterval(heartbeat)
    const previous = socket
    socket = null
    if (previous) { previous.onclose = null; previous.close() }
    chatUnread.value = 0
    updateChatBadge(0)
    baseline = false
    chatConnection.value = 'disabled'
  }
  if (changed) window.dispatchEvent(new Event('chat-controls-changed'))
}

export const refreshChatControls = async () => {
  if (!isAuthValid()) return false
  const version = generation
  const data = await chatApi('/control-status')
  if (version !== generation) return chatEnabled.value
  const previous = chatEnabled.value
  applyChatEnabled(Boolean(data.enabled))
  if (running && chatEnabled.value && (!previous || !socket)) connect()
  return chatEnabled.value
}

export const refreshChatUnread = async () => {
  const version = generation
  if (!isAuthValid() || !chatEnabled.value) return
  try {
    const data = await chatApi('/unread')
    if (version !== generation || !running) return
    const previous = chatUnread.value
    chatUnread.value = Math.max(0, Number(data.count || 0))
    updateChatBadge(chatUnread.value)
    const mentioned = Number(data.last_mention_id || 0) > lastAttention
    const important = Math.max(Number(data.last_reply_id || 0), Number(data.last_mention_id || 0)) > lastAttention
    lastAttention = Math.max(lastAttention, Number(data.last_mention_id || 0), Number(data.last_reply_id || 0))
    if (baseline && (important || (chatUnread.value > previous && (location.pathname !== '/chat' || document.hidden)))) {
      ElNotification({ title: mentioned ? '有人 @ 你' : important ? '有消息待回复' : '收到新消息', message: important ? '打开沟通查看提醒，免打扰仍会提醒' : '打开沟通查看未读消息', duration: important ? 7000 : 4000,
        onClick: () => window.dispatchEvent(new Event('chat-open')) })
    }
    baseline = true
  } catch (_) { /* Reconnect/fallback will retry; do not interrupt other modules. */ }
}

const scheduleRefresh = event => {
  window.dispatchEvent(new CustomEvent('chat-event', { detail: event }))
  clearTimeout(refreshTimer)
  refreshTimer = setTimeout(refreshChatUnread, 300)
}

const connect = () => {
  if (!running || !chatEnabled.value || !isAuthValid() || socket || navigator.onLine === false) return
  const version = generation
  chatConnection.value = 'connecting'
  const current = new WebSocket(`${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/api/chat/ws`)
  socket = current
  current.onopen = () => {
    if (version !== generation) { current.close(); return }
    current.send(JSON.stringify({ type: 'authenticate', token: getAuthToken(), device_id: getAuthDeviceId() }))
    heartbeat = setInterval(() => {
      if (Date.now() - lastFrame > 60000) { current.close(); return }
      if (current.readyState === WebSocket.OPEN) current.send(JSON.stringify({ type: 'ping' }))
    }, 25000)
  }
  current.onmessage = event => {
    if (version !== generation) return
    lastFrame = Date.now()
    let value
    try { value = JSON.parse(event.data) } catch (_) { return }
    if (value.type === 'pong') return
    if (value.type === 'module_disabled') { applyChatEnabled(false); return }
    if (value.type === 'ready') { chatConnection.value = 'connected'; attempts = 0 }
    if (value.type === 'disabled') {
      // The authenticated me endpoint handles revoked sessions consistently.
      fetch('/api/auth/me').catch(() => {})
      return
    }
    scheduleRefresh(value)
  }
  current.onclose = async event => {
    if (version !== generation) return
    socket = null
    clearInterval(heartbeat)
    chatConnection.value = chatEnabled.value ? 'disconnected' : 'disabled'
    if (event.code === 4004) { applyChatEnabled(false); return }
    if (event.code === 4001 || event.code === 4003) {
      try {
        const response = await fetch('/api/auth/me')
        if (response.ok) {
          const data = await response.json()
          const user = data.user || data
          if (user.id && user.role !== getAuthUser().role) {
            saveAuth({ token: getAuthToken(), expires_at: getAuthExpiresAt(), user })
          }
        }
      } catch (_) { /* Retry when the network returns. */ }
    }
    if (!running || !chatEnabled.value || version !== generation) return
    retryTimer = setTimeout(connect, Math.min(15000, 1000 * 2 ** Math.min(attempts++, 4)) + Math.random() * 500)
  }
  current.onerror = () => current.close()
}

const wake = () => {
  if (document.hidden) return
  refreshChatControls().then(enabled => { if (enabled) refreshChatUnread() }).catch(() => {})
  if (socket && Date.now() - lastFrame > 40000) socket.close()
  connect()
  scheduleRefresh({ type: 'resume' })
}
const offline = () => {
  chatConnection.value = 'disconnected'
  socket?.close()
}

export const startChatClient = () => {
  stopChatClient()
  if (!isAuthValid()) return
  running = true
  lastFrame = Date.now()
  window.addEventListener('online', wake)
  window.addEventListener('offline', offline)
  document.addEventListener('visibilitychange', wake)
  // A proxy without WebSocket support still gets eventual delivery, without
  // eighty users polling while their sockets are healthy.
  fallbackTimer = setInterval(() => {
    refreshChatControls().catch(() => {})
    if (chatEnabled.value && !document.hidden && chatConnection.value !== 'connected') scheduleRefresh({ type: 'fallback' })
  }, 15000)
  const version = generation
  refreshChatControls().then(enabled => {
    if (!running || version !== generation || !enabled) return
    initializeChatPush(chatApi)
    connect()
    refreshChatUnread()
  }).catch(() => {})
}

export const stopChatClient = () => {
  running = false
  clearChatPushBinding()
  generation++
  baseline = false
  lastAttention = 0
  attempts = 0
  clearTimeout(retryTimer)
  clearTimeout(refreshTimer)
  clearInterval(heartbeat)
  clearInterval(fallbackTimer)
  window.removeEventListener('online', wake)
  window.removeEventListener('offline', offline)
  document.removeEventListener('visibilitychange', wake)
  const previous = socket
  socket = null
  if (previous) { previous.onclose = null; previous.close() }
  chatUnread.value = 0
  chatConnection.value = 'disconnected'
}

export const compressChatImage = async file => {
  if (file.size > 25 * 1024 * 1024) throw new Error('原图超过 25MB，请先缩小图片')
  const url = URL.createObjectURL(file)
  try {
    const source = new Image()
    source.src = url
    await source.decode().catch(() => { throw new Error('无法读取图片，请转换为 JPG、PNG 或 WebP') })
    if (source.naturalWidth * source.naturalHeight > 25000000) throw new Error('图片像素过大，请先缩小')
    const canvas = document.createElement('canvas')
    for (const edge of [2048, 1600, 1280, 1024, 768]) {
      const scale = Math.min(1, edge / Math.max(source.naturalWidth, source.naturalHeight))
      canvas.width = Math.max(1, Math.round(source.naturalWidth * scale))
      canvas.height = Math.max(1, Math.round(source.naturalHeight * scale))
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = '#fff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(source, 0, 0, canvas.width, canvas.height)
      for (const quality of [0.88, 0.75, 0.6, 0.45]) {
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/webp', quality))
        if (blob && blob.size <= 1000000) return blob
      }
    }
    throw new Error('图片无法压缩到 1MB，请缩小后重试')
  } finally { URL.revokeObjectURL(url) }
}
